import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import type { CatalogEntry } from '../../services/inferenceApi';
import { getGenerationSchema } from '../../services/inferenceApi';
import { formatPrice, formatCurrencyAmount } from '../../utils/catalog';
import {
  PANEL_CONFIG, mergeAdvancedParams, readFileAsDataURI, estimateQuantity, estimateCost,
} from '../../utils/generation';
import { parseInputSchema, buildBodyFromSchemaValues, missingRequired, SchemaField } from '../../utils/schemaForm';
import { buildGenerationSnippets, GenerationSnippets } from '../../utils/generationSnippets';
import { copyText } from '../../utils/clipboard';
import { env } from '../../config/env';
import { useGeneration } from '../../hooks/useGeneration';
import MediaResult from './MediaResult';
import SchemaFields from './SchemaFields';

type CodeLang = keyof GenerationSnippets;
const CODE_LANGS: CodeLang[] = ['curl', 'javascript', 'python'];

// Seeds the controlled values map from each field's parsed default so every
// widget starts life controlled (no undefined -> defined React warnings) -
// chips/boolean/json all need a widget-shaped empty value rather than a bare
// undefined.
function seedSchemaValues(fields: SchemaField[]): Record<string, unknown> {
  const seed: Record<string, unknown> = {};
  for (const field of fields) {
    switch (field.widget) {
      case 'chips':
        seed[field.name] = Array.isArray(field.defaultValue) ? field.defaultValue : [];
        break;
      case 'boolean':
        seed[field.name] = typeof field.defaultValue === 'boolean' ? field.defaultValue : false;
        break;
      case 'json':
        seed[field.name] = field.defaultValue !== undefined ? JSON.stringify(field.defaultValue, null, 2) : '';
        break;
      case 'number':
      case 'integer':
        seed[field.name] =
          typeof field.defaultValue === 'number' || typeof field.defaultValue === 'string'
            ? field.defaultValue
            : '';
        break;
      case 'select':
        if (typeof field.defaultValue === 'string') {
          seed[field.name] = field.defaultValue;
        } else if (field.required && field.enumValues && field.enumValues.length > 0) {
          // A required select with no schema default still renders with its
          // first <option> selected in the DOM (native <select> behavior) -
          // without this, state stays '' forever and missingRequired blocks
          // submit until the user touches a field that already looks filled.
          seed[field.name] = field.enumValues[0];
        } else {
          seed[field.name] = '';
        }
        break;
      default:
        seed[field.name] = typeof field.defaultValue === 'string' ? field.defaultValue : '';
    }
  }
  return seed;
}

interface GenerationPanelProps {
  entry: CatalogEntry;
  apiKeyOverride: string | null;
  currency: string;
}

const MAX_AUDIO_BYTES = 5 * 1024 * 1024;

export default function GenerationPanel({ entry, apiKeyOverride, currency }: GenerationPanelProps) {
  // Fallback to the image config shape if the category is unrecognized -
  // shouldn't happen given routing, but keeps the panel rendering something
  // sane (prompt + Generate) rather than crashing on a missing config.
  const config = PANEL_CONFIG[entry.category] ?? PANEL_CONFIG.image;
  const { runs, submit, refresh, isSubmitting } = useGeneration(entry.id, entry.category, apiKeyOverride);

  // Schema-driven form: only fetched for models flagged has_input_schema.
  // Loading and error/404 (data null) both fall back to the existing
  // category form below - the fetch is intentionally silent on failure
  // (no toast), since a schema-less fal model is an expected, common case,
  // not an error state worth surfacing.
  const {
    data: schemaData,
    isLoading: schemaLoading,
    isError: schemaErrored,
  } = useQuery({
    queryKey: ['generation-schema', entry.id],
    queryFn: () => getGenerationSchema(entry.id, apiKeyOverride),
    enabled: entry.has_input_schema,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const schemaFields = useMemo(() => (schemaData ? parseInputSchema(schemaData) : []), [schemaData]);
  // SCHEMA MODE only once a non-null schema resolved into at least one
  // renderable field - a present-but-empty/malformed schema degrades to the
  // category form the same as a 404, rather than rendering nothing.
  const schemaMode = entry.has_input_schema && !schemaLoading && !schemaErrored && schemaFields.length > 0;

  const [schemaValues, setSchemaValues] = useState<Record<string, unknown>>({});
  // One-shot per schema load: without this guard, a background refetch
  // (react-query can still re-run the query - e.g. window refocus - even
  // under a 5 min staleTime) that resolves a new `schemaFields` array
  // identity would silently clobber whatever the user has already typed.
  // The panel also fully remounts per entry.id (see Playground's
  // key={entry.id}), so this never needs to react to `entry` directly - a
  // genuine model switch always gets a fresh ref via remount, not this flag.
  const seededRef = useRef(false);
  useEffect(() => {
    if (schemaFields.length > 0 && !seededRef.current) {
      setSchemaValues(seedSchemaValues(schemaFields));
      seededRef.current = true;
    }
  }, [schemaFields]);
  const handleSchemaChange = (name: string, value: unknown) => {
    setSchemaValues((prev) => ({ ...prev, [name]: value }));
  };

  // Duration is a metering param (excluded from schemaFields) rendered by
  // the panel itself - but some models constrain it to fixed choices in
  // their input schema (LTX 2.3: enum [6,8,10], default 6). When the schema
  // declares a numeric enum, the free number input becomes a select of
  // exactly those values, or fal 422s the request after submit.
  const durationChoices = useMemo(() => {
    const props = (schemaData as { properties?: Record<string, { enum?: unknown[]; default?: unknown }> } | null | undefined)
      ?.properties;
    const prop = props?.duration;
    if (!prop || !Array.isArray(prop.enum)) return null;
    const values = prop.enum.filter((v): v is number => typeof v === 'number');
    if (values.length === 0) return null;
    return {
      values,
      defaultValue: typeof prop.default === 'number' && values.includes(prop.default) ? prop.default : values[0],
    };
  }, [schemaData]);

  const [prompt, setPrompt] = useState('');
  const [extraUrl, setExtraUrl] = useState('');
  const [audioDataUri, setAudioDataUri] = useState('');
  const [audioFilename, setAudioFilename] = useState('');
  const [advancedJSON, setAdvancedJSON] = useState('');

  // Resolution slab selector (tiered entries only) - default tier preselected.
  const [resolution, setResolution] = useState<string>(() => {
    if (entry.price_tiers.length === 0) return '';
    return entry.price_tiers.find((t) => t.default)?.key ?? entry.price_tiers[0].key;
  });
  // Duration is shared by per_video_second (required, defaulted to 5) and
  // per_audio_minute (optional, starts blank) - a given entry only has one
  // billing_unit, so only one of those input fields ever renders at a time.
  const [duration, setDuration] = useState<string>(entry.billing_unit === 'per_video_second' ? '5' : '');
  const [numFrames, setNumFrames] = useState<string>(entry.billing_unit === 'per_100_frames' ? '100' : '');

  // One-shot like seededRef above: when the schema resolves with fixed
  // duration choices and the current value isn't one of them (the generic
  // '5' seed rarely is), snap to the schema's default. Never re-fires on
  // background refetches, so a user's pick is never clobbered.
  const durationSeededRef = useRef(false);
  useEffect(() => {
    if (!durationChoices || durationSeededRef.current) return;
    durationSeededRef.current = true;
    if (!durationChoices.values.some((v) => String(v) === duration)) {
      setDuration(String(durationChoices.defaultValue));
    }
  }, [durationChoices, duration]);

  const price = formatPrice(entry, currency);

  // Parsed numeric values (undefined when blank/invalid) feed both the
  // submit body and the live estimate below, keeping the two in sync.
  const durationValue = duration.trim() !== '' && !Number.isNaN(Number(duration)) ? Number(duration) : undefined;
  const numFramesValue = numFrames.trim() !== '' && !Number.isNaN(Number(numFrames)) ? Number(numFrames) : undefined;
  const estimatedQuantity = estimateQuantity(entry.billing_unit, { duration: durationValue, num_frames: numFramesValue });
  const estimatedCost = estimateCost(entry, resolution, estimatedQuantity);

  // mergeAdvancedParams is pure/cheap, so re-deriving the validity on every
  // keystroke (rather than only at submit time) is what lets the inline
  // error and the submit-disabled state both reflect live typing.
  const advancedError = useMemo(() => {
    try {
      mergeAdvancedParams({}, advancedJSON);
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : 'Invalid JSON';
    }
  }, [advancedJSON]);

  // Data URI takes precedence over the typed URL, for both the submitted
  // body and the "is audio provided" check.
  const resolvedAudioValue = audioDataUri || extraUrl.trim();

  const promptMissing = !schemaMode && !config.noPrompt && prompt.trim() === '';
  const audioMissing = !schemaMode && config.extraInput === 'audio_url' && resolvedAudioValue === '';

  // missingRequired doesn't flag an empty-array required chips field (`[]`
  // isn't undefined/null/'') - per the task-4 handoff, the panel has to add
  // that check itself on top.
  const schemaMissingLabels = useMemo(() => {
    if (!schemaMode) return [];
    const base = missingRequired(schemaFields, schemaValues);
    const emptyChips = schemaFields
      .filter(
        (f) =>
          f.widget === 'chips' &&
          f.required &&
          Array.isArray(schemaValues[f.name]) &&
          (schemaValues[f.name] as unknown[]).length === 0
      )
      .map((f) => f.label);
    return Array.from(new Set([...base, ...emptyChips]));
  }, [schemaMode, schemaFields, schemaValues]);

  // buildBodyFromSchemaValues throws a labeled Error on an invalid json
  // widget - re-derived live (like advancedError) so the inline error and
  // submit-disabled state both track typing, not just submit attempts.
  const schemaBodyError = useMemo(() => {
    if (!schemaMode) return null;
    try {
      buildBodyFromSchemaValues(schemaFields, schemaValues);
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : 'Invalid value';
    }
  }, [schemaMode, schemaFields, schemaValues]);

  const submitDisabled =
    isSubmitting ||
    advancedError !== null ||
    promptMissing ||
    audioMissing ||
    (schemaMode && (schemaMissingLabels.length > 0 || schemaBodyError !== null));

  const loadAudioFile = async (file: File) => {
    try {
      const dataUri = await readFileAsDataURI(file, MAX_AUDIO_BYTES);
      setAudioDataUri(dataUri);
      setAudioFilename(file.name);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to read file');
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) void loadAudioFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) void loadAudioFile(file);
  };

  const clearAudioFile = () => {
    setAudioDataUri('');
    setAudioFilename('');
  };

  // Single source of truth for the submit body - both handleSubmit and the
  // "API code" sample panel below call this, so the curl/js/python samples
  // always describe exactly what Generate would actually send. Returns an
  // error string (rather than throwing) on invalid JSON in either a schema
  // json widget or the Advanced parameters field, since the code panel needs
  // to keep rendering something even mid-typo.
  const buildCurrentBody = (): { body: Record<string, unknown> } | { error: string } => {
    let body: Record<string, unknown>;
    if (schemaMode) {
      try {
        body = buildBodyFromSchemaValues(schemaFields, schemaValues);
      } catch (e) {
        return { error: e instanceof Error ? e.message : 'Invalid value' };
      }
    } else {
      body = {};
      // prompt is required whenever !noPrompt, so by the time submitDisabled
      // is false, prompt is guaranteed non-empty here - and noPrompt configs
      // (transcription) never send an empty prompt field.
      if (!config.noPrompt) body[config.promptField] = prompt;
      if (config.extraInput) {
        // audio_url prefers the data URI over the typed URL; image_url just uses the typed URL.
        const value = config.extraInput === 'audio_url' ? resolvedAudioValue : extraUrl.trim();
        if (value) body[config.extraInput] = value;
      }
    }
    // Slab/quantity params, only when set - these mirror the exact param
    // names ExtractMetering reads server-side (resolution/duration/num_frames).
    // Same wiring in both modes: metering params are excluded from parsed
    // schema fields, so there's never a key collision with the schema body.
    if (resolution) body.resolution = resolution;
    if (durationValue !== undefined) body.duration = durationValue;
    if (numFramesValue !== undefined) body.num_frames = numFramesValue;
    try {
      // Advanced JSON is merged last, so a conflicting key there still wins
      // over the resolution/duration/num_frames set above.
      return { body: mergeAdvancedParams(body, advancedJSON) };
    } catch (e) {
      return { error: e instanceof Error ? e.message : 'Invalid JSON' };
    }
  };

  const handleSubmit = async () => {
    if (submitDisabled) return;
    const result = buildCurrentBody();
    if ('error' in result) {
      // Guarded by submitDisabled (schemaBodyError/advancedError) above;
      // defensive no-op.
      return;
    }
    await submit(result.body);
  };

  const [codeLang, setCodeLang] = useState<CodeLang>('curl');
  // The API-code panel needs something to render even mid-typo (e.g. an
  // unclosed brace in Advanced JSON, or a schema json widget that doesn't
  // parse yet) - it falls back to the last body that *did* build
  // successfully rather than crashing or blanking the samples. Recomputing
  // buildCurrentBody() here is cheap (no I/O, small object), so this runs on
  // every render rather than being memoized.
  const lastValidBodyRef = useRef<Record<string, unknown>>({});
  const currentBodyResult = buildCurrentBody();
  if ('body' in currentBodyResult) lastValidBodyRef.current = currentBodyResult.body;
  const snippetBody = 'body' in currentBodyResult ? currentBodyResult.body : lastValidBodyRef.current;
  const snippets = buildGenerationSnippets({
    modelId: entry.id,
    displayName: entry.display_name,
    body: snippetBody,
    baseUrl: env.INFERENCE_API_URL.replace(/\/$/, ''),
  });

  return (
    <div className="space-y-4">
      <div className="pixel-card space-y-3">
        {schemaMode && (
          <SchemaFields fields={schemaFields} values={schemaValues} onChange={handleSchemaChange} />
        )}

        {!schemaMode && !config.noPrompt && (
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="generation-prompt">
              {config.promptLabel}
            </label>
            <textarea
              id="generation-prompt"
              className="input"
              rows={3}
              placeholder={config.promptPlaceholder}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </div>
        )}

        {!schemaMode && config.extraInput === 'image_url' && (
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="generation-image-url">
              Source image URL (optional)
            </label>
            <input
              id="generation-image-url"
              type="url"
              className="input"
              value={extraUrl}
              onChange={(e) => setExtraUrl(e.target.value)}
            />
          </div>
        )}

        {!schemaMode && config.extraInput === 'audio_url' && (
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="generation-audio-url">
              Audio URL
            </label>
            <input
              id="generation-audio-url"
              type="url"
              className="input"
              value={extraUrl}
              onChange={(e) => setExtraUrl(e.target.value)}
              disabled={Boolean(audioDataUri)}
            />
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              className="mt-2 rounded-lg border border-dashed border-gray-700 p-3 text-center text-xs text-gray-500"
            >
              {audioFilename ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-gray-300">{audioFilename}</span>
                  <button
                    type="button"
                    onClick={clearAudioFile}
                    aria-label="Clear selected audio file"
                    className="text-gray-500 hover:text-white"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  or drop an audio file - max 5 MB
                  <input type="file" accept="audio/*" className="hidden" onChange={handleFileInput} />
                </label>
              )}
            </div>
          </div>
        )}

        {entry.price_tiers.length > 0 && (
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="generation-resolution">
              Resolution
            </label>
            <select
              id="generation-resolution"
              className="input"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
            >
              {entry.price_tiers.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.key}{t.default ? ' (default)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {entry.billing_unit === 'per_video_second' && (
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="generation-duration">
              Duration (seconds)
            </label>
            {durationChoices ? (
              <select
                id="generation-duration"
                className="input"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              >
                {durationChoices.values.map((v) => (
                  <option key={v} value={String(v)}>
                    {v}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id="generation-duration"
                type="number"
                min={1}
                step={1}
                className="input"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            )}
          </div>
        )}

        {entry.billing_unit === 'per_100_frames' && (
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="generation-frames">
              Frames
            </label>
            <input
              id="generation-frames"
              type="number"
              min={1}
              className="input"
              value={numFrames}
              onChange={(e) => setNumFrames(e.target.value)}
            />
          </div>
        )}

        {entry.billing_unit === 'per_audio_minute' && (
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="generation-duration-audio">
              Duration (seconds) (optional)
            </label>
            <input
              id="generation-duration-audio"
              type="number"
              min={1}
              className="input"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </div>
        )}

        <details>
          <summary className="cursor-pointer select-none text-xs font-medium text-gray-400">
            Advanced parameters (JSON)
          </summary>
          <textarea
            className="input mt-2 font-mono text-xs"
            rows={4}
            placeholder='{"seed": 42}'
            value={advancedJSON}
            onChange={(e) => setAdvancedJSON(e.target.value)}
            aria-invalid={advancedError !== null}
            aria-label="Advanced parameters JSON"
          />
          {advancedError && <p className="mt-1 text-xs text-red-400">{advancedError}</p>}
        </details>

        <details>
          <summary className="cursor-pointer select-none text-xs font-medium text-gray-400">
            API code
          </summary>
          <div className="mt-2 space-y-2">
            <div className="flex gap-1 rounded-lg bg-gray-900 p-1 text-xs">
              {CODE_LANGS.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  className={`flex-1 rounded-md py-1 capitalize ${
                    codeLang === lang ? 'bg-gray-700 text-white' : 'text-gray-400'
                  }`}
                  onClick={() => setCodeLang(lang)}
                >
                  {lang}
                </button>
              ))}
            </div>
            <div className="relative">
              <pre className="text-xs font-mono whitespace-pre overflow-x-auto rounded-lg bg-gray-950 p-3 text-gray-300">
                {snippets[codeLang]}
              </pre>
              <button
                type="button"
                aria-label="Copy code sample"
                className="absolute right-2 top-2 rounded-md bg-gray-800 px-2 py-1 text-xs text-gray-400 hover:text-white"
                onClick={() => void copyText(snippets[codeLang])}
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-gray-500">Create an API key in the console → API keys.</p>
          </div>
        </details>

        {price && <div className="font-mono text-xs text-gray-400">{price.input}</div>}
        {estimatedCost !== null && (
          <div
            className="font-mono text-xs text-gray-400"
            title="The server computes the authoritative charge using these same metering rules."
          >
            Estimated: {formatCurrencyAmount(entry, estimatedCost, currency)}
          </div>
        )}

        {schemaMode && schemaBodyError && <p className="text-xs text-red-400">{schemaBodyError}</p>}
        {schemaMode && !schemaBodyError && schemaMissingLabels.length > 0 && (
          <p className="text-xs text-gray-500">Missing required: {schemaMissingLabels.join(', ')}</p>
        )}

        <button type="button" className="btn-primary" onClick={() => void handleSubmit()} disabled={submitDisabled}>
          {config.submitLabel}
        </button>
      </div>

      {runs.map((run) => (
        <MediaResult key={run.id} run={run} onRefresh={refresh} />
      ))}
    </div>
  );
}
